/**
 * Copy for the Add User generated-ID overlay and intake radios (pure — no React).
 */

import { generatedStudentIdHint } from './studentIdIntake';
import { generatedUnixUsernameHint } from './unixUsernameFormat';

const SEQUENCE_PLACEHOLDER = '0001';

export const GENERATED_ID_OVERLAY_EXAMPLES = Object.freeze({
  studentId: 'ON26TI0001',
  unixUsername: 'po0826_user_1',
});

/**
 * Live Student ID / Unix username as enrollment options change.
 * Uses the preview API only when its prefix matches the current selection
 * (so the sequence can update without showing a stale ID).
 */
export function liveGeneratedIdentity({
  categoryType,
  categoryCode,
  modeOfCourse,
  locationOfCenter,
  firstName,
  lastName,
  batchNumber,
  batchName,
  batchId,
  previewBatchId,
  previewId,
  previewUnixUsername,
  now = new Date(),
} = {}) {
  const hint = generatedStudentIdHint({
    categoryType,
    categoryCode,
    modeOfCourse,
    locationOfCenter,
    now,
  });
  const apiId = String(previewId || '')
    .trim()
    .toUpperCase();
  const prefixMatches = Boolean(hint.prefix) && Boolean(apiId) && apiId.startsWith(hint.prefix);
  const studentId = prefixMatches
    ? apiId
    : hint.prefix
      ? `${hint.prefix}${SEQUENCE_PLACEHOLDER}`
      : '';
  const unixLocal = studentId
    ? generatedUnixUsernameHint({
        programType: hint.program,
        modeOfCourse,
        locationOfCenter,
        firstName,
        lastName,
        batchNumber,
        batchName,
        previewId: studentId,
        now,
      })
    : '';
  const apiUnix = String(previewUnixUsername || '').trim();
  const previewIsForThisBatch =
    String(previewBatchId || '') === String(batchId || '');
  return {
    studentId,
    unixUsername:
      studentId && prefixMatches && apiUnix && previewIsForThisBatch ? apiUnix : unixLocal,
    prefix: hint.prefix,
    ready: Boolean(hint.prefix),
  };
}

export const STUDENT_ID_INTAKE_RADIO_HELP = Object.freeze({
  generate:
    'Pick a Batch. Online IDs start with ON (ON26TI0001). Offline IDs start with OF plus center N/B/M/C (OFN26PO0001 is Offline + Noida, not a site code). Unix username po0826_user_1.',
  existing:
    'Type the Student ID this person already has. A Unix username is still attached from name, batch, and that ID.',
  lead: 'Keep the current create path. No generated ID or Unix username unless you type an ID. Batch enrollment is optional.',
});

export const GENERATED_ID_OVERLAY_NOTES = Object.freeze([
  'Role = student',
  'Student ID and Unix username follow the selection below and are assigned on save.',
  'Assigned on save. If two people save at the same time, the next free values are used.',
]);

/** Plain text for clipboard after a successful register. */
export function formatRegisteredUserClipboardText({
  studentId,
  unixUsername,
  email,
  name,
  role,
  password,
} = {}) {
  const lines = [
    name ? `Name: ${String(name).trim()}` : null,
    email ? `Email: ${String(email).trim()}` : null,
    role ? `Role: ${String(role).trim()}` : null,
    studentId ? `Student ID: ${String(studentId).trim()}` : null,
    unixUsername ? `Unix username: ${String(unixUsername).trim()}` : null,
    password ? `Password: ${String(password).trim()}` : null,
  ].filter(Boolean);
  return lines.join('\n');
}

export function overlaySelectionRows({
  programType,
  courseName,
  mode,
  center,
  referralLabel,
  batchName,
  personName,
} = {}) {
  const online = String(mode || '').trim().toLowerCase() === 'online';
  return [
    { label: 'Program', value: programType },
    { label: 'Course', value: courseName },
    { label: 'Mode', value: mode },
    { label: 'Center', value: online ? 'Online' : center },
    { label: 'Referred by', value: referralLabel },
    { label: 'Batch', value: batchName },
    { label: 'Name', value: personName },
  ].map((row) => ({
    label: row.label,
    value: String(row.value || '').trim() || '—',
  }));
}

/**
 * @param {{
 *   studentId?: unknown,
 *   unixUsername?: unknown,
 *   selection?: {
 *     programType?: unknown,
 *     mode?: unknown,
 *     center?: unknown,
 *     referralLabel?: unknown,
 *     batchName?: unknown,
 *     personName?: unknown,
 *   },
 * }} [assigned]
 * @returns {{
 *   title: string,
 *   studentId: string,
 *   unixUsername: string,
 *   details: { label: string, value: string }[],
 *   notes: readonly string[],
 * }}
 */
export function generatedIdOverlayCopy({ studentId, unixUsername, selection } = {}) {
  const assignedId = String(studentId || '').trim();
  const assignedUnix = String(unixUsername || '').trim();
  return {
    title: assignedId ? 'Suggested IDs from the enrollment fields' : 'Suggested Student ID',
    studentId: assignedId,
    unixUsername: assignedUnix,
    details: overlaySelectionRows(selection),
    notes: GENERATED_ID_OVERLAY_NOTES,
  };
}
